import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect if the current device is mobile
 * @param breakpoint - The breakpoint in pixels to consider as mobile (default: 768px)
 * @param onBreakpointChange - Optional callback fired when crossing the breakpoint.
 *   Receives the new isMobile value. Runs inside the resize event handler,
 *   so it is safe to call setState from it.
 * @returns boolean - true if mobile, false if desktop
 */
const useMobile = (
  breakpoint: number = 768,
  onBreakpointChange?: (isMobile: boolean) => void,
): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );

  // Keep the callback ref stable so we don't re-subscribe on every render
  const onChangeRef = useRef(onBreakpointChange);
  onChangeRef.current = onBreakpointChange;

  useEffect(() => {
    let prevMobile = window.innerWidth < breakpoint;

    const checkIsMobile = () => {
      const nowMobile = window.innerWidth < breakpoint;
      setIsMobile(nowMobile);

      if (nowMobile !== prevMobile) {
        prevMobile = nowMobile;
        onChangeRef.current?.(nowMobile);
      }
    };

    // Check on initial load
    checkIsMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIsMobile);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, [breakpoint]);

  return isMobile;
};

export default useMobile;
